import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDossiers } from '../../lib/hooks';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  Search, 
  FolderOpen, 
  Loader2, 
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Eye,
  FileCheck,
  FileWarning,
  FileClock,
  FileX
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const visaLabels = (lang = 'fr') => ({
  studies: lang === 'de' ? 'Studium' : 'Études',
  formation_pro: lang === 'de' ? 'Berufsausbildung' : 'Formation Pro',
  work: lang === 'de' ? 'Arbeit' : 'Travail',
  family_reunification: lang === 'de' ? 'Familienzusammenführung' : 'Regroupement familial',
});

const visaColors = {
  studies: 'bg-blue-100 text-blue-700',
  formation_pro: 'bg-purple-100 text-purple-700',
  work: 'bg-orange-100 text-orange-700',
  family_reunification: 'bg-green-100 text-green-700',
};

const statusConfig = (lang = 'fr') => ({
  incomplete: { label: lang === 'de' ? 'Unvollständig' : 'Incomplet', color: 'bg-slate-100 text-slate-600', icon: FileX },
  in_progress: { label: lang === 'de' ? 'In Bearbeitung' : 'En cours', color: 'bg-yellow-100 text-yellow-700', icon: FileClock },
  complete: { label: lang === 'de' ? 'Vollständig' : 'Complet', color: 'bg-success/10 text-success', icon: FileCheck },
  pdf_generated: { label: lang === 'de' ? 'PDF erstellt' : 'PDF généré', color: 'bg-primary/10 text-primary', icon: FileCheck },
});

const DossierList = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    visa_objective: '',
    status: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const { data: dossiers, isLoading, error } = useDossiers({
    page,
    per_page: 20,
    search: filters.search || undefined,
    visa_objective: filters.visa_objective || undefined,
    status: filters.status || undefined,
  });

  const resetFilters = () => {
    setFilters({ search: '', visa_objective: '', status: '' });
    setPage(1);
  };

  const hasActiveFilters = filters.visa_objective || filters.status;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(lang === 'de' ? 'de-DE' : 'fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const visaLabelsMap = visaLabels(lang);
  const statusConfigMap = statusConfig(lang);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dossiers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-3xl font-bold text-foreground">{t('dossiers_title', lang)}</h1>
          {dossiers && (
            <Badge variant="secondary" className="text-base px-3">
              {dossiers.length}
            </Badge>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t("students_search", lang)}
                  value={filters.search}
                  onChange={(e) => {
                    setFilters({ ...filters, search: e.target.value });
                    setPage(1);
                  }}
                  className="pl-10"
                  data-testid="dossier-search-input"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className={hasActiveFilters ? 'border-primary text-primary' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                {lang === 'de' ? 'Filter' : 'Filtres'}
                {hasActiveFilters && <Badge className="ml-2 bg-primary text-white">!</Badge>}
                {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
              </Button>
            </div>

            {showFilters && (
              <div className="flex flex-col md:flex-row gap-4 pt-4 border-t animate-slide-up">
                <Select 
                  value={filters.visa_objective} 
                  onValueChange={(value) => {
                    setFilters({ ...filters, visa_objective: value === 'all' ? '' : value });
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full md:w-48" data-testid="visa-filter">
                    <SelectValue placeholder={lang === 'de' ? 'Visumziel' : 'Objectif visa'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{lang === 'de' ? 'Alle Ziele' : 'Tous objectifs'}</SelectItem>
                    <SelectItem value="studies">{lang === 'de' ? 'Studium' : 'Études'}</SelectItem>
                    <SelectItem value="formation_pro">{lang === 'de' ? 'Berufsausbildung' : 'Formation Pro'}</SelectItem>
                    <SelectItem value="work">{lang === 'de' ? 'Arbeit' : 'Travail'}</SelectItem>
                    <SelectItem value="family_reunification">{lang === 'de' ? 'Familienzusammenführung' : 'Regroupement familial'}</SelectItem>
                  </SelectContent>
                </Select>

                <Select 
                  value={filters.status} 
                  onValueChange={(value) => {
                    setFilters({ ...filters, status: value === 'all' ? '' : value });
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                    <SelectValue placeholder={lang === 'de' ? 'Status' : 'Statut'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{lang === 'de' ? 'Alle Status' : 'Tous statuts'}</SelectItem>
                    <SelectItem value="incomplete">{lang === 'de' ? 'Unvollständig' : 'Incomplet'}</SelectItem>
                    <SelectItem value="in_progress">{lang === 'de' ? 'In Bearbeitung' : 'En cours'}</SelectItem>
                    <SelectItem value="complete">{lang === 'de' ? 'Vollständig' : 'Complet'}</SelectItem>
                    <SelectItem value="pdf_generated">{lang === 'de' ? 'PDF erstellt' : 'PDF généré'}</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="ghost" onClick={resetFilters} className="text-muted-foreground">
                  <X className="w-4 h-4 mr-2" />
                  {lang === 'de' ? 'Zurücksetzen' : 'Réinitialiser'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-destructive">
          <p>{lang === 'de' ? 'Fehler beim Laden der Akten' : 'Erreur lors du chargement des dossiers'}</p>
        </div>
      ) : dossiers?.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="font-heading text-xl font-semibold mb-1">{lang === 'de' ? 'Keine Akten gefunden' : 'Aucun dossier trouvé'}</h3>
          <p className="text-muted-foreground">
            {hasActiveFilters || filters.search 
              ? (lang === 'de' ? 'Ändern Sie Ihre Filter, um mehr Ergebnisse anzuzeigen' : 'Modifiez vos filtres pour voir plus de résultats')
              : (lang === 'de' ? 'Die Akten der Studierenden werden hier angezeigt' : 'Les dossiers des étudiants apparaîtront ici')
            }
          </p>
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{lang === 'de' ? 'Studierende/r' : 'Étudiant'}</TableHead>
                <TableHead>{lang === 'de' ? 'Visumziel' : 'Objectif visa'}</TableHead>
                <TableHead>{lang === 'de' ? 'Vollständigkeit' : 'Complétion'}</TableHead>
                <TableHead>{lang === 'de' ? 'Status' : 'Statut'}</TableHead>
                <TableHead>{lang === 'de' ? 'Letzte Aktivität' : 'Dernière activité'}</TableHead>
                <TableHead className="text-right">{lang === 'de' ? 'Aktionen' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dossiers?.map((dossier) => {
                const StatusIcon = statusConfigMap[dossier.status]?.icon || FileX;
                return (
                  <TableRow 
                    key={dossier.student_id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/dossiers/${dossier.student_id}`)}
                    data-testid={`dossier-row-${dossier.student_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary text-white">
                            {dossier.student_name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{dossier.student_name}</p>
                          <p className="text-sm text-muted-foreground">{dossier.student_email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={visaColors[dossier.visa_objective]}>
                        {visaLabelsMap[dossier.visa_objective] || dossier.visa_objective}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Progress value={dossier.completion_percentage} className="h-2 w-24" />
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          {dossier.validated_count}/{dossier.total_required} {lang === 'de' ? 'bestätigt' : 'validés'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusConfigMap[dossier.status]?.color}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfigMap[dossier.status]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(dossier.last_activity)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dossiers/${dossier.student_id}`);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {lang === 'de' ? 'Ansehen' : 'Voir'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default DossierList;
